import { cn } from "@client/utils/cn";
import {
  Text,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@sfc/ui";
import {
  CheckIcon,
  ChevronRightIcon,
  CircleSlashIcon,
  MoreHorizontalIcon,
} from "lucide-react";
import { type ReactNode } from "react";
import { type TodoStep } from "@lib/todoRail/todoRailModel";
import { TodoProgressRing } from "./TodoProgressRing";

/**
 * The lengths that decide where the spine runs.
 *
 * The line is drawn by the ROW, absolutely and over its full height, so
 * consecutive rows meet with no seam. The node it has to pass through is drawn
 * by the BUTTON inside that row. Those are two different boxes, so the offset
 * used to be a pixel measured in the browser — and the row's padding, the
 * node's size and the rule's thickness could each be changed without anything
 * noticing that the line no longer landed on the dots.
 *
 * So the lengths are declared once here and APPLIED to both sides: the button
 * takes its border and padding from this object, the node cell takes its size,
 * and {@link spineLeft} is computed from the same values rather than measured
 * against them.
 *
 * They keep the UNITS of the Tailwind tokens they replace, which for
 * everything but the border means `rem`. Tailwind's spacing scale is
 * `0.25rem`, so `size-7` and `px-2` grow with the reader's root font size and
 * so does the dot inside the cell — a cell fixed at 28px would be overflowed
 * by its own 4px focus ring at any larger text setting. That is also why the
 * offset is a CSS `calc()` rather than a number: it sums a px border with rem
 * lengths, so only the browser can add it up. The measured pixel it replaces
 * could not, which is why the line already sat ~5px off the dots at a 20px
 * root font size.
 */
const rowGeometry = {
  /** The row button's border. Tailwind `border`, which is px, not spacing. */
  borderWidth: "1px",
  /** The row button's horizontal padding. Tailwind `px-2`. */
  paddingX: "0.5rem",
  /** The square cell the node dot is centred in. Tailwind `size-7`. */
  nodeSize: "1.75rem",
  /** The spine rule's thickness. Tailwind `w-0.5`. */
  spineWidth: "0.125rem",
};

/**
 * Where the rule's LEFT edge goes, so that its centre lands on the node's.
 * The node's centre is the button's inset plus half the node cell.
 */
const spineLeft = `calc(${rowGeometry.borderWidth} + ${rowGeometry.paddingX} + ${rowGeometry.nodeSize} / 2 - ${rowGeometry.spineWidth} / 2)`;

/**
 * One row on the spine.
 *
 * The spine is a 2px rule drawn by each row, so consecutive rows form one
 * continuous line whose colour changes at the boundary between finished and
 * unfinished work. Drawing it per-row (rather than as one absolutely-positioned
 * element behind the list) is what lets an expanded group push the line down
 * with it — the line is made of the rows, so it can't fall out of step with
 * them.
 *
 * Each row draws its line as two halves that MEET AT THE NODE: one from the
 * row's top edge down to its mid-line, one from the mid-line to its bottom. The
 * mid-line is where the node is, because the row centres it — so the line finds
 * the node without anyone having to know how tall the row is. That matters
 * here: these titles wrap to two lines and carry a meta line under them, so a
 * row can be any height.
 *
 * A line between nodes has to START at a node and END at one: `isFirst` and
 * `isLast` are what drop the half that would dangle. They describe the row's
 * place among ITS OWN siblings — the phase's rows at the top level, the group's
 * rows inside an expanded group — because a child list is as much a run of
 * connected nodes as the top level is.
 */
export function TodoSpineRow({
  step,
  depth,
  isCurrent,
  isFirst,
  isLast,
  isExpanded,
  onToggleExpand,
  onOpen,
  onSetDone,
  children,
}: {
  step: TodoStep;
  depth: number;
  isCurrent: boolean;
  /** First row among its siblings that draws the spine. */
  isFirst: boolean;
  /** Last row among its siblings that draws the spine. */
  isLast: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOpen: () => void;
  /**
   * Sets this step's real value, or undefined when the step's state is real
   * work that can't be asserted (a score, an allocation).
   */
  onSetDone?: (done: boolean) => void;
  /** The step's child rows, rendered by the caller. */
  children?: ReactNode;
}) {
  const done = step.progress.done;
  const expandable = step.children.length > 0;

  // Observed marks are a RECORD, not a control: the app ticks them when the
  // funder actually opens the notes or the profile summary (see
  // `useRecordTodoObservation`). Rendering them as checkboxes invited a funder
  // to assert they'd read something they hadn't, and made the rail's honesty
  // depend on their bookkeeping.
  if (step.kind === "observed") {
    // They still carry a destination: each observed mark names a specific
    // place on the recommender's page (the notes card, the profile card, the
    // score field), so the row that asks for the work also opens it.
    const Row = step.href != null ? "button" : "div";
    return (
      // The menu is a SIBLING of the row, not inside it: the row is itself a
      // button when it has a destination, and a button inside a button is
      // invalid markup that swallows the inner click.
      <div className="flex items-center gap-1 pl-10">
        <Row
          {...(step.href != null
            ? { type: "button" as const, onClick: onOpen }
            : {})}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 rounded-md py-1 text-left",
            step.href != null &&
              "hover:bg-surface-elevated focus-visible:outline-2 focus-visible:outline-primary",
          )}
        >
          <span
            aria-hidden
            className={cn(
              "grid size-4 flex-none place-items-center rounded-full border",
              done
                ? "border-success bg-success text-white"
                : "border-border-strong",
            )}
          >
            {done && <CheckIcon className="size-2.5" strokeWidth={4} />}
          </span>
          <span
            className={cn(
              "text-body-sm",
              done ? "text-text-muted" : "text-text-secondary",
            )}
          >
            {step.title}
          </span>
        </Row>
        <TodoStepMenu onSetDone={onSetDone} title={step.title} />
      </div>
    );
  }

  return (
    <div>
      {/* A run of one has nothing to connect to, so both halves are dropped and
          the row draws no line at all. */}
      <div className="relative flex items-center gap-3">
        {!isFirst && <SpineSegment done={done} placement="above" />}
        {!isLast && <SpineSegment done={done} placement="below" />}
        {/* The whole row is the target, not just the chevron. A row with a
            destination opens it; a grouping row with none (a recommender)
            expands — otherwise that row is inert and the only way in is a
            28px arrow, which is a poor target on the iPad this is read on. */}
        <button
          type="button"
          onClick={
            step.href != null ? onOpen : expandable ? onToggleExpand : undefined
          }
          disabled={step.href == null && !expandable}
          style={{
            borderWidth: rowGeometry.borderWidth,
            paddingLeft: rowGeometry.paddingX,
            paddingRight: rowGeometry.paddingX,
          }}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-3 rounded-lg border-transparent py-2 text-left",
            (step.href != null || expandable) &&
              "hover:border-border-subtle hover:bg-surface-elevated",
            step.href == null && !expandable && "cursor-default",
          )}
        >
          {/* The node. Sits above the spine and is the row's state at a glance:
              filled + ticked when done, ringed when this is where you are,
              hollow otherwise. */}
          <span
            data-todo-node
            style={{
              width: rowGeometry.nodeSize,
              height: rowGeometry.nodeSize,
            }}
            className="relative z-1 grid flex-none place-items-center"
          >
            <span
              className={cn(
                "grid place-items-center rounded-full border-2 bg-surface-base transition-colors",
                done
                  ? "size-4.5 border-success bg-success text-white"
                  : isCurrent
                    ? "size-4.5 border-primary ring-4 ring-accent-subtle"
                    : "size-3 border-border-strong",
              )}
            >
              {done && <CheckIcon className="size-2.5" strokeWidth={4} />}
              {!done && isCurrent && (
                <span className="size-1.75 rounded-full bg-primary" />
              )}
            </span>
          </span>

          <span className="flex min-w-0 flex-1 flex-col">
            {/* Wraps rather than truncates. These titles are numbered and
                near-identical ("Main Meeting #2.5 Highlights"), so the part
                that gets cut is the only part that distinguishes them — a row
                that is taller is strictly better than a row you can't tell
                apart from the one above it. */}
            <span
              className={cn(
                "text-body-sm leading-snug font-medium break-words",
                done && "text-text-secondary",
              )}
            >
              {step.title}
            </span>
            {/* Optional extras say so — they're excluded from their step's
                progress, so skipping every one still reaches 100%, and the
                label is what makes that legible rather than surprising.
                It sits on the META line, not beside the title: these titles
                ("Main Meeting #2.5 Highlights") are long, and a chip competing
                for the same row truncated the one thing you need to read. */}
            {(step.optional || step.progress.detail !== "") && (
              <Text
                as="span"
                variant="body-sm"
                tone="muted"
                className="flex min-w-0 flex-wrap items-center gap-x-1.5"
              >
                {step.optional && (
                  <Text
                    as="span"
                    variant="label-sm"
                    tone="muted"
                    className="flex-none leading-none tracking-wider uppercase opacity-70"
                  >
                    Optional
                  </Text>
                )}
                <span className="tabular-nums">{step.progress.detail}</span>
              </Text>
            )}
          </span>

          {/* A group of sub-tasks reads as a wheel: its meta line already spells
              out "2 of 3 done", so repeating that as a large figure on the
              right was the same fact twice, taking the room the title needs. */}
          {step.kind === "recommender" && !done && (
            <TodoProgressRing
              fraction={step.progress.fraction}
              size={18}
              strokeWidth={4}
            />
          )}

          {/* Everything else reads twice — as a bar and as a figure — because
              the bar is scannable down the column while the figure is the exact
              value. Both come from the same number. */}
          {step.kind !== "recommender" &&
            step.progress.fraction > 0 &&
            !done && (
              <span
                className="h-1 w-8 flex-none overflow-hidden rounded-full bg-border-subtle"
                aria-hidden
              >
                <span
                  className="block h-full rounded-full bg-primary"
                  style={{
                    width: `${Math.round(step.progress.fraction * 100)}%`,
                  }}
                />
              </span>
            )}
          {step.kind !== "recommender" && (
            <span
              className={cn(
                "flex-none text-label-sm tabular-nums",
                done ? "text-success-text" : "text-text-secondary",
              )}
            >
              {step.progress.label}
            </span>
          )}
        </button>

        <TodoStepMenu onSetDone={onSetDone} title={step.title} />

        {expandable && (
          <button
            type="button"
            onClick={onToggleExpand}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${step.title}`}
            className="grid size-9 flex-none place-items-center rounded-md text-text-muted hover:bg-surface-hover hover:text-text-primary"
          >
            <ChevronRightIcon
              className={cn(
                "size-3.5 transition-transform duration-200 motion-reduce:transition-none",
                isExpanded && "rotate-90",
              )}
            />
          </button>
        )}
      </div>

      {expandable && isExpanded && (
        <div className={cn("relative pb-2", depth === 0 ? "pl-10" : "pl-6")}>
          {/* An expanded group's children sit between this row's node and the
              next sibling's, so the line carries on through them — which is
              also why they render inside this row rather than after it. */}
          {!isLast && <SpineSegment done={done} placement="through" />}
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * One length of the spine.
 *
 * `above` and `below` are the halves either side of a row's node, and `through`
 * is the full-height run behind an expanded group's children. All three are the
 * same rule; only where they stop differs.
 */
function SpineSegment({
  done,
  placement,
}: {
  done: boolean;
  placement: "above" | "below" | "through";
}) {
  return (
    <span
      aria-hidden
      data-spine-segment={placement}
      style={{ left: spineLeft, width: rowGeometry.spineWidth }}
      className={cn(
        // Horizontal placement is not a class: it is computed from the same
        // lengths the row itself is built from — see `rowGeometry`.
        "absolute",
        done ? "bg-success" : "bg-border-subtle",
        placement === "above" && "top-0 bottom-1/2",
        placement === "below" && "top-1/2 bottom-0",
        placement === "through" && "inset-y-0",
      )}
    />
  );
}

/**
 * "Mark as complete" / "Mark as not done" for one step.
 *
 * Rendered for every step whose value the funder can actually set — including
 * the observed marks, which is the point: an app that decides on your behalf
 * that you have read something must let you say otherwise. Steps backed by real
 * work (a score, an allocation) pass no handler and get no menu.
 *
 * "Mark as not done" is a reset rather than an exemption — the detector re-arms
 * and can tick the step again once the funder really does the thing.
 */
function TodoStepMenu({
  onSetDone,
  title,
}: {
  onSetDone?: (done: boolean) => void;
  title: string;
}) {
  if (onSetDone == null) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <button
            type="button"
            aria-label={`Change status of ${title}`}
            className="grid size-9 flex-none place-items-center rounded-md text-text-muted hover:bg-surface-hover hover:text-text-primary"
          />
        }
      >
        <MoreHorizontalIcon className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuItem onClick={() => onSetDone(true)}>
          <CheckIcon className="size-4" />
          Mark as complete
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onSetDone(false)}>
          <CircleSlashIcon className="size-4" />
          Mark as not done
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
